/**
 * AdminTrialsPage — admin overzicht van ALLE 30-dagen trials.
 *
 * Functies:
 *  - Lijst met filter (status: alles/actief/verlopen/geconverteerd)
 *  - Tellers: actief / converted / verlopen / annulering
 *  - Klantkolom + activator + status + acties (cancel)
 *  - Read-only voor andere rollen — admin/manager kunnen forceer-cancellen.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'sonner';
import {
  Ticket, Clock, CheckCircle2, XCircle, RefreshCw, Loader2, Search, Filter,
} from 'lucide-react';

const STATUS_MAP = {
  actief: { color: 'text-emerald-400', bg: 'bg-emerald-500/10', icon: Clock, label: 'Actief' },
  bijna_verlopen: { color: 'text-amber-400', bg: 'bg-amber-500/10', icon: Clock, label: 'Bijna verlopen' },
  verlopen: { color: 'text-red-400', bg: 'bg-red-500/10', icon: XCircle, label: 'Verlopen' },
  geconverteerd: { color: 'text-blue-400', bg: 'bg-blue-500/10', icon: CheckCircle2, label: 'Geconverteerd' },
  geannuleerd: { color: 'text-white/40', bg: 'bg-white/5', icon: XCircle, label: 'Geannuleerd' },
};

export default function AdminTrialsPage() {
  const { api } = useAuth();
  const [trials, setTrials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [search, setSearch] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api('get', `/api/team/trials${statusFilter ? `?status=${statusFilter}` : ''}`);
      setTrials(r.data.trials || []);
    } catch {
      toast.error('Trials laden mislukt');
    }
    setLoading(false);
  }, [api, statusFilter]);

  useEffect(() => { load(); }, [load]);

  const filtered = trials.filter((t) => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (t.klant_email || '').toLowerCase().includes(q) || (t.klant_name || '').toLowerCase().includes(q);
  });

  const stats = {
    actief: trials.filter((t) => t.status === 'actief' || t.status === 'bijna_verlopen').length,
    geconverteerd: trials.filter((t) => t.status === 'geconverteerd').length,
    verlopen: trials.filter((t) => t.status === 'verlopen').length,
    totaal: trials.length,
  };

  const handleCancel = async (id) => {
    if (!confirm('Weet u zeker dat u deze trial wilt annuleren?')) return;
    try {
      await api('post', `/api/team/trials/${id}/cancel`);
      toast.success('Trial geannuleerd');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Annuleren mislukt');
    }
  };

  return (
    <div className="min-h-screen bg-[#0f172a] text-white">
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-8">
        <div className="flex items-start justify-between mb-6 flex-wrap gap-3">
          <div>
            <p className="text-[#D4A017] text-xs uppercase tracking-wider font-bold mb-1">Admin</p>
            <h1 className="text-3xl md:text-4xl font-bold" style={{ fontFamily: "'Sora', sans-serif" }}>
              Alle trials
            </h1>
            <p className="text-white/60 text-sm mt-1">Overzicht van alle Self-Service trials uit de hiërarchie</p>
          </div>
          <button onClick={load} className="bg-white/5 hover:bg-white/10 border border-white/10 text-white rounded-lg px-3 py-2 text-sm flex items-center gap-1.5" data-testid="admin-trials-refresh">
            <RefreshCw size={14} /> Refresh
          </button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
          {[
            { label: 'Actief', value: stats.actief, color: 'text-emerald-400', icon: Clock },
            { label: 'Geconverteerd', value: stats.geconverteerd, color: 'text-blue-400', icon: CheckCircle2 },
            { label: 'Verlopen', value: stats.verlopen, color: 'text-red-400', icon: XCircle },
            { label: 'Totaal', value: stats.totaal, color: 'text-[#D4A017]', icon: Ticket },
          ].map((s) => (
            <div key={s.label} className="bg-white/5 rounded-xl p-4 border border-white/5" data-testid={`admin-trials-stat-${s.label.toLowerCase()}`}>
              <div className="flex items-center gap-2 mb-1">
                <s.icon size={14} className={s.color} />
                <p className="text-white/50 text-xs uppercase tracking-wide font-semibold">{s.label}</p>
              </div>
              <p className={`text-2xl font-bold ${s.color}`} style={{ fontFamily: "'Sora', sans-serif" }}>{s.value}</p>
            </div>
          ))}
        </div>

        {/* Filters */}
        <div className="flex items-center gap-2 mb-5 flex-wrap">
          <div className="relative flex-1 min-w-[200px]">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Zoek op e-mail of naam"
              className="w-full bg-white/5 border border-white/10 rounded-lg pl-9 pr-3 py-2 text-sm text-white placeholder-white/40"
              data-testid="admin-trials-search"
            />
          </div>
          <div className="flex items-center gap-2">
            <Filter size={14} className="text-white/40" />
            <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white" data-testid="admin-trials-status-filter">
              <option value="">Alle</option>
              <option value="actief">Actief</option>
              <option value="bijna_verlopen">Bijna verlopen</option>
              <option value="verlopen">Verlopen</option>
              <option value="geconverteerd">Geconverteerd</option>
              <option value="geannuleerd">Geannuleerd</option>
            </select>
          </div>
        </div>

        {/* Lijst */}
        {loading ? (
          <div className="text-white/40 flex items-center gap-2"><Loader2 className="animate-spin" size={16} /> Laden…</div>
        ) : filtered.length === 0 ? (
          <div className="bg-white/5 rounded-xl p-12 text-center text-white/40" data-testid="admin-trials-empty">Geen trials gevonden.</div>
        ) : (
          <div className="bg-white/5 rounded-xl border border-white/10 overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-white/5">
                <tr className="text-left text-white/50 text-xs uppercase tracking-wider">
                  <th className="px-4 py-3">Klant</th>
                  <th className="px-4 py-3">Activator</th>
                  <th className="px-4 py-3">Start</th>
                  <th className="px-4 py-3">Einde</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/5">
                {filtered.map((t) => {
                  const s = STATUS_MAP[t.status] || STATUS_MAP.actief;
                  const Icon = s.icon;
                  return (
                    <tr key={t.id} className="hover:bg-white/5" data-testid={`admin-trial-row-${t.id}`}>
                      <td className="px-4 py-3">
                        <p className="text-white">{t.klant_name || t.klant_email}</p>
                        <p className="text-white/40 text-xs font-mono">{t.klant_email}</p>
                      </td>
                      <td className="px-4 py-3">
                        <p className="text-white/70 text-xs">{t.rol_aanmaker}</p>
                        <p className="text-white/40 text-[10px] font-mono">{t.aangemaakt_door_user_id?.slice(0, 8)}…</p>
                      </td>
                      <td className="px-4 py-3 text-white/70 text-xs">{t.startdatum?.slice(0, 10)}</td>
                      <td className="px-4 py-3 text-white/70 text-xs">{t.einddatum?.slice(0, 10)}</td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs ${s.color} ${s.bg}`}>
                          <Icon size={10} /> {s.label}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right">
                        {(t.status === 'actief' || t.status === 'bijna_verlopen') && (
                          <button
                            onClick={() => handleCancel(t.id)}
                            className="text-red-400 hover:text-red-300 text-xs font-semibold"
                            data-testid={`admin-cancel-trial-${t.id}`}
                          >
                            Annuleren
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
